import type { AdminSupportTicketDetail, AdminSupportTicketList, SupportMessageView } from "@ichegou/contracts";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Centrifuge } from "centrifuge";
import { api, call } from "./api.ts";

/** Staff support inbox (module `suporte`) over the contract in packages/contracts/src/support.ts. */
export type TicketList = (typeof AdminSupportTicketList)["static"];
export type TicketItem = TicketList["items"][number];
export type TicketDetail = (typeof AdminSupportTicketDetail)["static"];
export type SupportMessage = (typeof SupportMessageView)["static"];
export type TicketStatus = TicketItem["status"];

export type ListStatus = TicketStatus | "active" | "all";

const ticket = (id: string) => api.admin.support.tickets({ id });
export const supportApi = {
  list: (q: { status?: ListStatus; app?: "rider" | "driver"; q?: string; topic?: TicketItem["topic"]; mine?: boolean; cursor?: string }) =>
    call(api.admin.support.tickets.get({ query: { status: q.status, app: q.app, q: q.q, topic: q.topic, assigned: q.mine ? "me" : undefined, cursor: q.cursor, limit: 40 } })) as Promise<TicketList>,
  get: (id: string) => call(ticket(id).get()) as Promise<TicketDetail>,
  send: (id: string, body: string) => call(ticket(id).messages.post({ body })),
  assign: (id: string) => call(ticket(id).assign.post()),
  /** Hands the ticket to another member who answers support in its city (audited). */
  transfer: (id: string, staffUserId: string) => call(ticket(id).assign.post({ staffUserId })),
  assignees: (id: string) => call(ticket(id).assignees.get()),
  close: (id: string) => call(ticket(id).close.post()),
  refundOffer: (id: string, amountCents: number, note: string) => call(ticket(id)["refund-offer"].post({ amountCents, note })),
};

/**
 * Live inbox: `support.message` / `support.ticket_updated` on `admin:support` refresh the list and the
 * open conversation; polling stays as the fallback while the socket is down.
 */
export function useSupportRealtime() {
  const qc = useQueryClient();
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    let cancelled = false;
    let client: Centrifuge | null = null;
    const tokens = () => call(api.admin.support.connect.post());
    void tokens().then((t) => {
      if (cancelled) return;
      client = new Centrifuge(t.url, { token: t.token, getToken: async () => (await tokens()).token });
      const sub = client.newSubscription(t.channel, { token: t.subscriptionToken, getToken: async () => (await tokens()).subscriptionToken });
      sub.on("publication", (ctx) => {
        const ev = ctx.data as { type: string; data: { ticketId?: string } };
        if (ev.type !== "support.message" && ev.type !== "support.ticket_updated") return;
        void qc.invalidateQueries({ queryKey: ["support", "list"] });
        void qc.invalidateQueries({ queryKey: ["support", "badge"] });
        if (ev.data.ticketId) void qc.invalidateQueries({ queryKey: ["support", "ticket", ev.data.ticketId] });
      });
      sub.on("subscribed", () => setConnected(true));
      sub.on("unsubscribed", () => setConnected(false));
      client.on("disconnected", () => setConnected(false));
      sub.subscribe();
      client.connect();
    }).catch(() => setConnected(false));
    return () => {
      cancelled = true;
      client?.disconnect();
      setConnected(false);
    };
  }, [qc]);
  return connected;
}

export { TICKET_STATUS, waited, SLA_SECONDS, parseCents } from "./support-labels.ts";
